<script>
    import { onMount, beforeUpdate, onDestroy } from "svelte";
    import { Chart, registerables } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { glucoseunit } from "$stores/unitsStore";
    import { convertGlucoseValue } from "$utils/unitUtils";
    import { get } from "svelte/store";
    import { selectedStudy, selectedParticipant } from "$stores/selectionStore";
    import { studyProperties, fetchStudyProperties } from "$stores/configStore";
    import { createChartPrintHandler, getChartOptions } from "$utils/chartPrintUtils";

    export let agpData = [];

    let canvas;
    let chart;
    let isInitialized = false;
    let printHandler;

    // Default thresholds (will be overridden by study properties)
    let HIGH_THRESHOLD = 180;
    let LOW_THRESHOLD = 70;

    const lightModeOptions = {
        scales: {
            x: {
                type: 'linear',
                min: 0,
                max: 1440, // 24 hours in minutes
                ticks: {
                    callback: (value) => {
                        const hours = Math.floor(value / 60);
                        return hours === 24 ? "24:00" : `${hours.toString().padStart(2, '0')}:00`;
                    },
                    stepSize: 120, // 2-hour intervals
                    color: "rgba(0, 0, 0, 0.8)",
                },
                title: {
                    display: false,
                    text: "Time of Day",
                }
            },
            y: {
                title: {
                    display: true,
                    text: "Glucose",
                    color: "rgba(0, 0, 0, 0.8)",
                },
                position: "right",
                ticks: {
                    color: "rgba(0, 0, 0, 0.8)",
                },
            },
        },
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: "rgba(0, 0, 0, 0.8)",
                }
            },
            // Remove hardcoded annotations - they will be set dynamically
            annotation: {
                annotations: {}
            },
        },
    };

    const darkModeOptions = {
        ...lightModeOptions,
        scales: {
            ...lightModeOptions.scales,
            x: {
                ...lightModeOptions.scales.x,
                ticks: {
                    ...lightModeOptions.scales.x.ticks,
                    color: "rgba(240, 240, 240, 0.8)",
                }
            },
            y: {
                ...lightModeOptions.scales.y,
                ticks: {
                    ...lightModeOptions.scales.y.ticks,
                    color: "rgba(240, 240, 240, 0.8)",
                },
                title: {
                    ...lightModeOptions.scales.y.title,
                    color: "rgba(240, 240, 240, 0.8)",
                }
            }
        },
        plugins: {
            ...lightModeOptions.plugins,
            legend: {
                ...lightModeOptions.plugins.legend,
                labels: {
                    color: "rgba(240, 240, 240, 0.8)",
                }
            }
        }
    };

    // Update chart configuration when units or study properties change
    $: if (chart && isInitialized) {
        const currentUnit = $glucoseunit;
        
        // Use study properties for thresholds if available
        const highThresholdValue = $studyProperties?.glucoseParameters?.thresholds?.high || HIGH_THRESHOLD;
        const lowThresholdValue = $studyProperties?.glucoseParameters?.thresholds?.low || LOW_THRESHOLD;
        const highThreshold = convertGlucoseValue(highThresholdValue, currentUnit);
        const lowThreshold = convertGlucoseValue(lowThresholdValue, currentUnit);
        
        // Update chart title
        chart.options.scales.y.title.text = `Glucose (${currentUnit})`;
        
        // Ensure annotation lines exist before updating them
        if (!chart.options.plugins.annotation.annotations.line1) {
            chart.options.plugins.annotation.annotations.line1 = {
                type: "line",
                borderColor: "red",
                borderWidth: 0.5,
            };
        }
        if (!chart.options.plugins.annotation.annotations.line2) {
            chart.options.plugins.annotation.annotations.line2 = {
                type: "line",
                borderColor: "orange",
                borderWidth: 0.5,
            };
        }
        
        chart.options.plugins.annotation.annotations.line1.yMin = highThreshold;
        chart.options.plugins.annotation.annotations.line1.yMax = highThreshold;
        chart.options.plugins.annotation.annotations.line2.yMin = lowThreshold;
        chart.options.plugins.annotation.annotations.line2.yMax = lowThreshold;
        
        // Update y-axis range based on unit
        if (currentUnit === 'mmol/L') {
            chart.options.scales.y.min = 0;
            chart.options.scales.y.max = 25;
        } else {
            chart.options.scales.y.min = 0;
            chart.options.scales.y.max = 450;
        }
        
        chart.update('none'); // Use 'none' to prevent animation
    }    function createChart() {
        
        try {
            // Use print mode flag from the print handler
            const options = getChartOptions(lightModeOptions, darkModeOptions, printHandler ? get(printHandler.isPrintMode) : false);
            
            // Set initial title with current unit
            options.scales.y.title.text = `Glucose (${$glucoseunit})`;
            
            // Use study properties for thresholds if available, otherwise use defaults
            const highThresholdValue = $studyProperties?.glucoseParameters?.thresholds?.high || HIGH_THRESHOLD;
            const lowThresholdValue = $studyProperties?.glucoseParameters?.thresholds?.low || LOW_THRESHOLD;
            const highThreshold = convertGlucoseValue(highThresholdValue, $glucoseunit);
            const lowThreshold = convertGlucoseValue(lowThresholdValue, $glucoseunit);
            
            // Set Y-axis range based on unit
            if ($glucoseunit === 'mmol/L') {
                options.scales.y.min = 0;
                options.scales.y.max = 25;
            } else {
                options.scales.y.min = 0;
                options.scales.y.max = 450;
            }
            
            const ctx = canvas.getContext("2d");

            // Convert data points based on current unit
            const convertedData = agpData.map(d => ({
                minute: d.minute,
                p5: d.p5 ? convertGlucoseValue(d.p5, $glucoseunit) : null,
                p25: d.p25 ? convertGlucoseValue(d.p25, $glucoseunit) : null,
                p50: d.p50 ? convertGlucoseValue(d.p50, $glucoseunit) : null,
                p75: d.p75 ? convertGlucoseValue(d.p75, $glucoseunit) : null,
                p95: d.p95 ? convertGlucoseValue(d.p95, $glucoseunit) : null
            }));

            // Map data using the minute value directly
            const p95Data = convertedData.map(d => ({ x: d.minute, y: d.p95 }));
            const p75Data = convertedData.map(d => ({ x: d.minute, y: d.p75 }));
            const p50Data = convertedData.map(d => ({ x: d.minute, y: d.p50 }));
            const p25Data = convertedData.map(d => ({ x: d.minute, y: d.p25 }));
            const p5Data = convertedData.map(d => ({ x: d.minute, y: d.p5 }));

            chart = new Chart(ctx, {
                type: 'line',
                data: {
                    datasets: [
                        {
                            label: '90% Range (p5-p95)',
                            data: p95Data,
                            fill: '+1',
                            backgroundColor: 'rgba(200, 200, 200, 0.2)',
                            borderWidth: 0,
                            pointRadius: 0,
                            tension: 0.5,
                            cubicInterpolationMode: 'default'
                        },
                        {
                            label: '90% Range Bottom',
                            data: p5Data,
                            fill: false,
                            borderWidth: 0,
                            pointRadius: 0,
                            tension: 0.5,
                            cubicInterpolationMode: 'default'
                        },
                        {
                            label: 'IQR (p25-p75)',
                            data: p75Data,
                            fill: '+1',
                            backgroundColor: 'rgba(200, 200, 200, 0.3)',
                            borderWidth: 0,
                            pointRadius: 0,
                            tension: 0.5,
                            cubicInterpolationMode: 'default'
                        },
                        {
                            label: 'IQR Bottom',
                            data: p25Data,
                            fill: false,
                            borderWidth: 0,
                            pointRadius: 0,
                            tension: 0.5,
                            cubicInterpolationMode: 'default'
                        },
                        {
                            label: 'Median',
                            data: p50Data,
                            fill: false,
                            borderColor: 'rgba(75, 192, 192, 1)',
                            borderWidth: 2,
                            pointRadius: 2,
                            tension: 0.5,
                            cubicInterpolationMode: 'default'
                        }
                    ]
                },
                options: {
                    ...options,
                    maintainAspectRatio: false,
                    plugins: {
                        ...options.plugins,
                        annotation: {
                            annotations: {
                                line1: {
                                    type: "line",
                                    yMin: highThreshold,
                                    yMax: highThreshold,
                                    borderColor: "red",
                                    borderWidth: 0.5,
                                },
                                line2: {
                                    type: "line",
                                    yMin: lowThreshold,
                                    yMax: lowThreshold,
                                    borderColor: "orange",
                                    borderWidth: 0.5,
                                },
                            },
                        },
                        legend: {
                            ...options.plugins.legend,
                            labels: {
                                ...options.plugins.legend.labels,
                                filter: function(legendItem, data) {
                                    // Only show certain labels in the legend
                                    return ['90% Range (p5-p95)', 'IQR (p25-p75)', 'Median'].includes(legendItem.text);
                                }
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    const point = context.raw;
                                    const glucose = point.y.toFixed(1);
                                    const minutes = point.x;
                                    const hours = Math.floor(minutes / 60);
                                    const mins = minutes % 60;
                                    const time = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                                    return `${context.dataset.label}: ${glucose} ${$glucoseunit} at ${time}`;
                                },
                                // Optional: Customize the title
                                title: function(tooltipItems) {
                                    return ''; // Remove default title if you want
                                }
                            }
                        }
                    }
                }
            });
            
        } catch (error) {
            console.error('AGPChart createChart error:', error);
        }
    }

    function updateChartData() {
        if (!chart || !isInitialized) return;
        
        // Convert data points based on current unit
        const convertedData = agpData.map(d => ({
            minute: d.minute,
            p5: d.p5 ? convertGlucoseValue(d.p5, $glucoseunit) : null,
            p25: d.p25 ? convertGlucoseValue(d.p25, $glucoseunit) : null,
            p50: d.p50 ? convertGlucoseValue(d.p50, $glucoseunit) : null,
            p75: d.p75 ? convertGlucoseValue(d.p75, $glucoseunit) : null,
            p95: d.p95 ? convertGlucoseValue(d.p95, $glucoseunit) : null
        }));

        // Map data using the minute value directly
        const p95Data = convertedData.map(d => ({ x: d.minute, y: d.p95 }));
        const p75Data = convertedData.map(d => ({ x: d.minute, y: d.p75 }));
        const p50Data = convertedData.map(d => ({ x: d.minute, y: d.p50 }));
        const p25Data = convertedData.map(d => ({ x: d.minute, y: d.p25 }));
        const p5Data = convertedData.map(d => ({ x: d.minute, y: d.p5 }));

        // Update chart data
        chart.data.datasets[0].data = p95Data;
        chart.data.datasets[1].data = p5Data;
        chart.data.datasets[2].data = p75Data;
        chart.data.datasets[3].data = p25Data;
        chart.data.datasets[4].data = p50Data;
        
        chart.update('none');
    }    onMount(async () => {
        // Fetch study properties to get glucose thresholds
        if ($selectedStudy?._id) {
            await fetchStudyProperties($selectedStudy._id);
        }
        
        Chart.register(...registerables, annotationPlugin);
        
        // Initialize print handler
        printHandler = createChartPrintHandler(
            () => {
                if (chart) {
                    chart.destroy();
                }
                createChart();
            },
            () => chart && isInitialized
        );
        
        // Wait for unit to be available
        
        // Don't create chart here - let the reactive statement handle it
        isInitialized = true;
    });

    // Handle data changes - only update if chart is initialized and all dependencies are ready
    $: {
        
        if (isInitialized && agpData.length > 0 && $glucoseunit && canvas) {
            if (chart) {
                updateChartData();
            } else {
                createChart();
            }
        }
    }    // Cleanup when component is destroyed
    onDestroy(() => {
        if (printHandler) {
            printHandler.cleanup();
        }
        if (chart) {
            chart.destroy();
        }
    });
</script>

<canvas bind:this={canvas} class="print:max-w-full print:bg-white"></canvas>